/**
 * buildCierreTicketModel.ts — Modelo único del ticket de cierre (preview, térmica, PDF).
 *
 * Centraliza cálculos contables y KPIs operativos. No importa React.
 */

import type { CierreCaja, Venta } from "../types/caja.types";
import { buildCierreAuditMetrics } from "./cierreAuditMetrics";
import { buildCierreTurnoResumen } from "./buildCierreTurnoResumen";
import {
  fechaContableFromIso,
  readGastosExternosActivosByFecha,
  sumGastosExternosActivos,
  type GastoExterno,
} from "./externalExpensesHelper";
import {
  groupPropinaPorEmpleado,
  groupVentasPorEmpleado,
  splitPropinaNegocioEmpleados,
  sumCobradoPorMetodo,
  sumNetoIngresoPorMetodo,
  sumPropinaPorMetodo,
  sumSubtotalPorMetodo,
  sumTarifasUsoPorMetodo,
  totalMontos,
  totalPropinaMontos,
  totalTarifasUso,
  turnoTieneTarifasUso,
  type MontosPorMetodoPago,
} from "./propinaAllocation";

export interface RendimientoEmpleadoRow {
  userName: string;
  ventasTotal: number;
  ventasCount: number;
  propinasTotal: number;
}

export interface CierreTicketModel {
  ventasCobradas: MontosPorMetodoPago;
  /** Neto real ingresado al negocio (post tarifas de pasarela). */
  ventasNetoIngreso: MontosPorMetodoPago;
  /** Comisiones de pasarela descontadas o recargadas al cliente. */
  tarifasUso: MontosPorMetodoPago;
  totalTarifasUso: number;
  tieneTarifasUso: boolean;
  ventasNetas: MontosPorMetodoPago;
  totalVentasNetas: number;
  totalDigitalCobrado: number;
  totalDigitalNetas: number;
  ventasActivasCount: number;
  propinas: MontosPorMetodoPago;
  propinaTotal: number;
  propinaReparto: ReturnType<typeof splitPropinaNegocioEmpleados>;
  propinaPorEmpleado: ReturnType<typeof groupPropinaPorEmpleado>;
  rendimientoEmpleados: RendimientoEmpleadoRow[];
  resumenOperativo: ReturnType<typeof buildCierreTurnoResumen>;
  audit: ReturnType<typeof buildCierreAuditMetrics>;
  contadoReal: number;
  esperadoGaveta: number;
  diferencia: number;
  recaudoEfectivo: number;
  recaudoDigital: number;
  totalRecaudado: number;
  totalIngresosTurno: number;
  totalSalidasFisicas: number;
  totalAnulaciones: number;
  /** Efectivo contado menos base elegida para el siguiente turno y propinas en efectivo. */
  efectivoNetoEntregar: number;
  /** Recaudo menos gastos y devoluciones del turno. */
  dineroEsperadoCierre: number;
  /** Efectivo físicamente contado más recaudo digital neto. */
  dineroEncontradoCierre: number;
  /** Encontrado - esperado para el resumen financiero oficial. */
  diferenciaFinancieraCierre: number;
  /** Gastos del dueño pagados fuera de gaveta (fecha contable del cierre). */
  gastosExternosDia: GastoExterno[];
  totalGastosExternosDia: number;
}

function mergeRendimientoEmpleados(
  ventas: ReturnType<typeof groupVentasPorEmpleado>,
  propinas: ReturnType<typeof groupPropinaPorEmpleado>,
): RendimientoEmpleadoRow[] {
  const map = new Map<string, RendimientoEmpleadoRow>();

  for (const v of ventas) {
    map.set(v.userName, {
      userName: v.userName,
      ventasTotal: v.total,
      ventasCount: v.count,
      propinasTotal: 0,
    });
  }

  for (const p of propinas) {
    const existing = map.get(p.userName);
    if (existing) {
      existing.propinasTotal = p.total;
    } else {
      map.set(p.userName, {
        userName: p.userName,
        ventasTotal: 0,
        ventasCount: 0,
        propinasTotal: p.total,
      });
    }
  }

  return [...map.values()].sort((a, b) => b.ventasTotal - a.ventasTotal);
}

export function buildCierreTicketModel(
  cierre: CierreCaja,
  ventas: Venta[],
): CierreTicketModel {
  const ventasActivas = ventas.filter((v) => v.estado !== "anulada");
  const ventasCobradas = sumCobradoPorMetodo(ventasActivas);
  const ventasNetoIngreso = sumNetoIngresoPorMetodo(ventasActivas);
  const tarifasUso = sumTarifasUsoPorMetodo(ventasActivas);
  const ventasNetas = sumSubtotalPorMetodo(ventasActivas);
  const propinas = sumPropinaPorMetodo(ventasActivas);
  const propinaTotal = totalPropinaMontos(propinas);
  const propinaReparto = splitPropinaNegocioEmpleados(propinaTotal);
  const propinaPorEmpleado = groupPropinaPorEmpleado(ventasActivas);
  const ventasPorEmpleado = groupVentasPorEmpleado(ventasActivas);
  const rendimientoEmpleados = mergeRendimientoEmpleados(
    ventasPorEmpleado,
    propinaPorEmpleado,
  );

  const audit = buildCierreAuditMetrics(cierre, ventas);
  const contadoReal = cierre.montoCierre ?? 0;
  /** Siempre recalcular desde ventas + movimientos (ignora montoEsperado legacy en LS). */
  const esperadoGaveta = audit.esperadoGaveta;
  const diferencia = contadoReal - esperadoGaveta;

  const fechaContableCierre = fechaContableFromIso(
    cierre.cerradaEn ?? cierre.abiertaEn,
  );
  const gastosExternosDia =
    readGastosExternosActivosByFecha(fechaContableCierre);
  const totalGastosExternosDia = sumGastosExternosActivos(fechaContableCierre);

  const totalDigitalCobrado =
    ventasCobradas.nequi + ventasCobradas.daviplata + ventasCobradas.tarjeta;

  const totalDigitalNetas =
    ventasNetas.nequi + ventasNetas.daviplata + ventasNetas.tarjeta;
  const efectivoNetoEntregar = audit.cuadranteNeto.efectivoNetoEntregar;
  const dineroEsperadoCierre =
    audit.totalRecaudado - audit.totalGastos - audit.totalDevoluciones;
  const dineroEncontradoCierre = contadoReal + audit.recaudoDigital;
  const diferenciaFinancieraCierre =
    dineroEncontradoCierre - dineroEsperadoCierre;

  return {
    ventasCobradas,
    ventasNetoIngreso,
    tarifasUso,
    totalTarifasUso: totalTarifasUso(tarifasUso),
    tieneTarifasUso: turnoTieneTarifasUso(ventasActivas),
    ventasNetas,
    totalVentasNetas: totalMontos(ventasNetas),
    totalDigitalCobrado,
    totalDigitalNetas,
    ventasActivasCount: ventasActivas.length,
    propinas,
    propinaTotal,
    propinaReparto,
    propinaPorEmpleado,
    rendimientoEmpleados,
    resumenOperativo: buildCierreTurnoResumen(ventas),
    audit,
    contadoReal,
    esperadoGaveta,
    diferencia,
    recaudoEfectivo: audit.recaudoEfectivo,
    recaudoDigital: audit.recaudoDigital,
    totalRecaudado: audit.totalRecaudado,
    totalIngresosTurno:
      audit.totalAbonosCreditoEfectivo +
      audit.totalAbonosCreditoDigital +
      audit.totalIngresosManualesEfectivo +
      audit.totalIngresosManualesDigital,
    totalSalidasFisicas:
      audit.totalRetirosSeguridad +
      audit.retirosOtros +
      audit.totalGastos +
      audit.totalDevolucionesEfectivo,
    totalAnulaciones: audit.totalAnulaciones,
    efectivoNetoEntregar,
    dineroEsperadoCierre,
    dineroEncontradoCierre,
    diferenciaFinancieraCierre,
    gastosExternosDia,
    totalGastosExternosDia,
  };
}
